/**
 * AllowanceTypeFormModal — Create/edit allowance type with FormModal pattern.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Hash, Type, FileText, Receipt, Repeat, Tag, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { FormModal, FormField, useVinylSubmit, MODAL_COLORS } from '@/components/modal';
import { http } from '@/network/http';
import {
  useCreateAllowanceType,
  useUpdateAllowanceType,
} from '../hooks';
import type { AllowanceType, AllowanceTypeFormData } from '../types';

interface AllowanceTypeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: AllowanceType | null;
}

export function AllowanceTypeFormModal({
  isOpen,
  onClose,
  editData,
}: AllowanceTypeFormModalProps) {
  const { t } = useTranslation();
  const createMutation = useCreateAllowanceType();
  const updateMutation = useUpdateAllowanceType();
  const isEdit = !!editData;

  const modalContentRef = useRef<HTMLDivElement>(null);
  const { isAnimating, submitWithAnimation } = useVinylSubmit({
    modalContentRef,
    vinylLabel: isEdit ? 'EDIT' : 'NEW',
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [description, setDescription] = useState('');
  const [isTaxable, setIsTaxable] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [category, setCategory] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [error, setError] = useState('');

  const { data: existingCategories = [] } = useQuery<string[]>({
    queryKey: ['allowance-types', 'categories'],
    queryFn: async () => {
      const { data } = await http.get<{ success: boolean; categories: string[] }>('/allowance-types/categories');
      return data.categories;
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setCode(editData.code);
        setName(editData.name);
        setNameEn(editData.nameEn || '');
        setDescription(editData.description || '');
        setIsTaxable(editData.isTaxable);
        setIsRecurring(editData.isRecurring);
        setCategory(editData.category || '');
      } else {
        setCode('');
        setName('');
        setNameEn('');
        setDescription('');
        setIsTaxable(false);
        setIsRecurring(false);
        setCategory('');
      }
      setError('');
      setAddingCategory(false);
      setNewCategory('');
    }
  }, [isOpen, editData]);

  const isFormValid = useMemo(() => {
    return !!(code.trim() && name.trim());
  }, [code, name]);

  const validateForm = (): boolean => {
    setError('');
    if (!code.trim()) { setError(t('allowanceTypes.codeRequired')); return false; }
    if (!name.trim()) { setError(t('allowanceTypes.nameRequired')); return false; }
    return true;
  };

  const handleSubmit = () => {
    const payload: AllowanceTypeFormData = {
      code: code.trim(),
      name: name.trim(),
      nameEn: nameEn.trim() || undefined,
      description: description.trim() || undefined,
      isTaxable,
      isRecurring,
      category: category.trim() || undefined,
    };

    submitWithAnimation({
      validate: validateForm,
      mutate: () =>
        isEdit
          ? updateMutation.mutateAsync({ id: editData!.id, data: payload })
          : createMutation.mutateAsync(payload),
      onSuccess: () => {
        toast.success(isEdit ? t('allowanceTypes.updateSuccess') : t('allowanceTypes.createSuccess'));
        onClose();
      },
      onError: (err: any) => {
        const msg = err.response?.data?.message || '';
        setError(msg || (isEdit ? t('allowanceTypes.updateError') : t('allowanceTypes.createError')));
      },
    });
  };

  const inputStyle = {
    backgroundColor: MODAL_COLORS.interactive,
    border: `1px solid ${MODAL_COLORS.border}`,
    color: MODAL_COLORS.text,
  };

  if (!isOpen) return null;

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('allowanceTypes.edit') : t('allowanceTypes.create')}
      isPending={isPending}
      isAnimating={isAnimating}
      onSubmit={handleSubmit}
      onValidate={validateForm}
      isFormValid={isFormValid}
      modalContentRef={modalContentRef}
      vinylLabel={isEdit ? 'EDIT' : 'NEW'}
      error={error}
    >
      <div className="space-y-3">
        <FormField icon={<Hash className="h-4 w-4" />} label={t('allowanceTypes.code')} required>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isEdit}
            placeholder={t('allowanceTypes.codePlaceholder')}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>

        <FormField icon={<Type className="h-4 w-4" />} label={t('allowanceTypes.name')} required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('allowanceTypes.namePlaceholder')}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>

        <FormField icon={<Type className="h-4 w-4" />} label={t('allowanceTypes.nameEn')}>
          <input
            type="text"
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            placeholder={t('allowanceTypes.nameEnPlaceholder')}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>

        <FormField icon={<FileText className="h-4 w-4" />} label={t('allowanceTypes.description')}>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('allowanceTypes.descriptionPlaceholder')}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' as const }}
          />
        </FormField>

        <FormField icon={<Tag className="h-4 w-4" />} label={t('allowanceTypes.category')}>
          {addingCategory ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder={t('allowanceTypes.newCategoryPlaceholder')}
                autoFocus
                className="flex-1 p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
                style={inputStyle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newCategory.trim()) {
                    e.preventDefault();
                    setCategory(newCategory.trim());
                    setAddingCategory(false);
                    setNewCategory('');
                  } else if (e.key === 'Escape') {
                    setAddingCategory(false);
                    setNewCategory('');
                  }
                }}
              />
              <button
                type="button"
                onClick={() => {
                  if (newCategory.trim()) {
                    setCategory(newCategory.trim());
                    setAddingCategory(false);
                    setNewCategory('');
                  }
                }}
                className="px-3 rounded-lg text-sm font-semibold text-white"
                style={{ backgroundColor: '#047857' }}
              >
                {t('common.add')}
              </button>
              <button
                type="button"
                onClick={() => { setAddingCategory(false); setNewCategory(''); }}
                className="px-3 rounded-lg text-sm font-semibold"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, color: MODAL_COLORS.text }}
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="flex-1 p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2 cursor-pointer"
                style={inputStyle}
              >
                <option value="">{t('allowanceTypes.selectCategory')}</option>
                {existingCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
                {category && !existingCategories.includes(category) && (
                  <option value={category}>{category}</option>
                )}
              </select>
              <button
                type="button"
                onClick={() => setAddingCategory(true)}
                className="flex items-center gap-1 px-3 rounded-lg text-sm font-semibold transition-colors hover:opacity-80"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, color: MODAL_COLORS.text }}
                title={t('allowanceTypes.addCategory')}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          )}
        </FormField>

        <FormField icon={<Receipt className="h-4 w-4" />} label={t('allowanceTypes.isTaxable')}>
          <button
            type="button"
            onClick={() => setIsTaxable(!isTaxable)}
            className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors cursor-pointer"
            style={{ backgroundColor: isTaxable ? 'var(--color-primary-500)' : MODAL_COLORS.border }}
          >
            <span
              className="inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform"
              style={{ transform: isTaxable ? 'translateX(24px)' : 'translateX(4px)' }}
            />
          </button>
        </FormField>

        <FormField icon={<Repeat className="h-4 w-4" />} label={t('allowanceTypes.isRecurring')}>
          <button
            type="button"
            onClick={() => setIsRecurring(!isRecurring)}
            className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors cursor-pointer"
            style={{ backgroundColor: isRecurring ? 'var(--color-primary-500)' : MODAL_COLORS.border }}
          >
            <span
              className="inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform"
              style={{ transform: isRecurring ? 'translateX(24px)' : 'translateX(4px)' }}
            />
          </button>
        </FormField>
      </div>
    </FormModal>
  );
}
